import { useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import BarcodeScanner from "@/components/BarcodeScanner";
import { lookupBarcode, UnknownBarcodeDialog } from "@/components/barcode/BarcodeFlow";
import { ScanLine, Undo2, Minus, Plus, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random());
const SEL = "w-full h-12 rounded-md border border-slate-700 bg-slate-900 px-3 text-base text-slate-100";
const num = (n) => Number(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 3 });
const diffText = (d) => (d > 0 ? `+${num(d)} fazla` : d < 0 ? `−${num(-d)} eksik` : "0 eşit");
const diffCls = (d) => (d > 0 ? "text-sky-300 bg-sky-950/50 border-sky-800" : d < 0 ? "text-rose-300 bg-rose-950/50 border-rose-800" : "text-slate-300 bg-slate-800 border-slate-700");

function DiffRow({ sys, n, k }) {
  const d = n - sys;
  return (
    <div className="grid grid-cols-3 gap-1 text-center text-xs mt-1">
      <div className="rounded bg-slate-900 border border-slate-800 py-1"><div className="text-slate-500">Sistem</div><div className="font-mono text-sm" data-testid={`sc-sys-${k}`}>{num(sys)}</div></div>
      <div className="rounded bg-slate-900 border border-slate-800 py-1"><div className="text-slate-500">Sayılan</div><div className="font-mono text-sm" data-testid={`sc-counted-${k}`}>{num(n)}</div></div>
      <div className={`rounded border py-1 ${diffCls(d)}`}><div className="opacity-70">Fark</div><div className="font-mono text-sm font-semibold" data-testid={`sc-diff-${k}`}>{diffText(d)}</div></div>
    </div>
  );
}

// Seri sayım: her okutma yalnız oturum sayacını +1 yapar; fiziksel stok yalnız "Sayımı Uygula" ile (mevcut stock-count/apply)
export function SerialCount({ warehouses }) {
  const [wh, setWh] = useState("");
  const [counts, setCounts] = useState({}); // key → {product, variant, size, n}
  const [log, setLog] = useState([]);       // okutma sırası (geri al)
  const [last, setLast] = useState(null);
  const [scan, setScan] = useState(false);
  const [code, setCode] = useState("");
  const [unknown, setUnknown] = useState("");
  const [busy, setBusy] = useState(false);
  const [countId, setCountId] = useState(uid);
  const lines = useMemo(() => Object.entries(counts).filter(([, c]) => c.n > 0), [counts]);

  const sysRef = useRef({}); // key → sayım öncesi sistem stoğu (snapshot, sabit)
  const bump = (key, d, info) => setCounts((s) => {
    const cur = s[key] || { ...info, n: 0 };
    return { ...s, [key]: { ...cur, n: Math.max(0, cur.n + d) } };
  });
  const snapshot = async (r, key) => {
    if (key in sysRef.current) return sysRef.current[key];
    const { data } = await api.get("/inventory/stock", { params: { product_id: r.product.id, variant_id: r.variant?.id || "", warehouse_id: wh } });
    sysRef.current[key] = Number(data?.quantity || 0);
    return sysRef.current[key];
  };
  const onCode = async (c) => {
    const v = (c || "").trim();
    if (!v) return;
    try {
      const r = await lookupBarcode(v);
      if (!r.found) { setUnknown(r.code); return; }
      const key = r.variant?.id || r.product.id;
      const sys = await snapshot(r, key);
      bump(key, 1, { product: r.product, variant: r.variant, size: r.size, sys });
      setLog((l) => [...l, key]);
      setLast({ key, label: `${r.product.name}${r.size ? ` · ${r.size}` : ""}` });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const undo = () => {
    const key = log[log.length - 1];
    if (!key) return;
    bump(key, -1);
    setLog((l) => l.slice(0, -1));
    setLast(null);
  };
  const apply = async () => {
    const detail = lines.map(([, c]) => `${c.product.name}${c.size ? ` · ${c.size}` : ""}: Sistem ${num(c.sys)} / Sayılan ${num(c.n)} / Fark ${diffText(c.n - c.sys)}`).join("\n");
    if (!window.confirm(`${lines.length} kalem için sayım sonucu stoğa uygulanacak. Okutulmayan ürünler değişmez.\n\n${detail}\n\nDevam?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/warehouses/${wh}/stock-count/apply`, {
        count_id: countId, note: "El Terminali seri sayım",
        items: lines.map(([, c]) => ({ product_id: c.product.id, variant_id: c.variant?.id || "", counted_qty: c.n })),
      });
      toast.success(`Sayım uygulandı (${data?.changed ?? lines.length} kalem)`);
      setCounts({}); setLog([]); setLast(null); setCountId(uid()); sysRef.current = {};
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 border border-amber-800/60 rounded-md p-4 bg-amber-950/10" data-testid="serial-count">
      <div className="text-lg font-semibold text-amber-200">Seri Sayım</div>
      <select value={wh} onChange={(e) => setWh(e.target.value)} disabled={log.length > 0} className={SEL} data-testid="sc-warehouse">
        <option value="">Sayım deposu seçin</option>
        {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
      </select>
      {wh && (
        <>
          <div className="flex gap-2">
            <Button onClick={() => setScan(true)} className="h-14 flex-1 rounded-md bg-amber-600 hover:bg-amber-500 text-lg" data-testid="sc-scan-btn">
              <ScanLine className="w-6 h-6 mr-2" /> Okut
            </Button>
            <Button onClick={undo} disabled={!log.length} variant="outline" className="h-14 rounded-md border-slate-700 bg-slate-900" data-testid="sc-undo-btn">
              <Undo2 className="w-5 h-5 mr-1" /> Geri Al
            </Button>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); onCode(code); setCode(""); }} className="flex gap-2">
            <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" placeholder="Barkod (elle / okuyucu)"
              className={`${SEL} font-mono`} data-testid="sc-code-input" />
            <Button type="submit" className="h-12 rounded-md bg-slate-800" data-testid="sc-code-submit">Ekle</Button>
          </form>
          {last && (
            <div className="rounded-md bg-emerald-950/40 border border-emerald-800 p-3" data-testid="sc-last">
              <div className="text-xs text-emerald-400">Son okutulan</div>
              <div className="text-base font-semibold">{last.label}</div>
              <div className="text-3xl font-mono text-emerald-300" data-testid="sc-last-count">{counts[last.key]?.n || 0}</div>
              {counts[last.key] && <DiffRow sys={counts[last.key].sys} n={counts[last.key].n} k="last" />}
            </div>
          )}
          <div className="space-y-1" data-testid="sc-lines">
            {lines.map(([k, c]) => (
              <div key={k} className="border-b border-slate-800 py-1.5" data-testid={`sc-line-${k}`}>
              <div className="flex items-center justify-between gap-2 text-sm">
                <div className="min-w-0"><div className="truncate">{c.product.name}</div><div className="text-xs text-slate-400 font-mono">{c.size || c.variant?.sku || "Varyantsız"}</div></div>
                <div className="flex items-center gap-1">
                  <button onClick={() => bump(k, -1)} className="w-9 h-9 rounded bg-slate-800" data-testid={`sc-dec-${k}`}><Minus className="w-4 h-4 mx-auto" /></button>
                  <span className="w-10 text-center font-mono text-lg" data-testid={`sc-qty-${k}`}>{c.n}</span>
                  <button onClick={() => bump(k, 1)} className="w-9 h-9 rounded bg-slate-800" data-testid={`sc-inc-${k}`}><Plus className="w-4 h-4 mx-auto" /></button>
                </div>
              </div>
              <DiffRow sys={c.sys} n={c.n} k={k} />
              </div>
            ))}
          </div>
          <Button onClick={apply} disabled={!lines.length || busy} className="w-full h-14 rounded-md bg-emerald-600 hover:bg-emerald-500 text-lg" data-testid="sc-apply-btn">
            {busy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle2 className="w-5 h-5 mr-2" />} Sayımı Uygula
          </Button>
        </>
      )}
      <BarcodeScanner open={scan} onOpenChange={setScan} onDetected={onCode} />
      <UnknownBarcodeDialog code={unknown} onClose={() => setUnknown("")} onAssigned={() => { const c = unknown; setUnknown(""); onCode(c); }} />
    </div>
  );
}
